import * as Yup from 'yup'
import text, {realTimeValidationText} from './validationText'
import validator from 'validator'
import monitoringTypes from '../data/monitoringTypes.json'
import conditionTypes from '../data/conditionTypes.json'
import protocols from '../data/protocols.json'
import authMethods from '../data/authMethods.json'
import regexPatterns from './regexPatterns'
import DateHelper, {compareTimes} from './date'

const fields = {
    RESOURCE_NAME: 'Resource name',
    URL: 'URL',
    HOST: 'Host',
    SERVICE_NAME: 'Service name',
    REGIONS: 'Regions',
    NAME: 'Name',
    SURNAME: 'Surname',
    METRIC: 'Metric',
    PREVIOUS_PASSWORD: 'Previous password',
    PROPOSED_PASSWORD: 'New password',
    CONFIRM_PASSWORD: 'Confirm new password',
    EMAIL: 'New username',
    CODE: 'Verification code',
    TEAM_NAME: 'Team name',
    FEEDBACK: 'Feedback',
    CHANNEL_EMAIL: 'New notification channel email',
    VOUCHER: 'Voucher',
    CHANNEL_TYPE: 'Type',
    WEBHOOK: 'Webhook',
    DESCRIPTION: 'Description',
    EVENT: 'event',
    REMINDER: 'option',
    REASON: 'Deletion reason',
    MEMBER_EMAIL: 'Email',
    KEYWORD: 'Keyword',
    METHODS: 'Methods',
    KEY: 'Key',
    VALUE: 'Value',
    INTEGRATION_TYPE: 'Integration type',
    API_KEY: 'API key',
    API_ID: 'API ID',
    INTEGRATION_PAGE_ID: 'Page ID',
    INTEGRATION: 'Integration',
    COMPONENTS: 'Components',
    STATUS: 'Status',
    TITLE: 'Title',
    PORT: 'Port',
    PROTOCOL: 'Protocol',
    DATA_TO_SEND: 'Data',
    STATUS_CODE: 'Status code',
    COUPON: 'Coupon',
    OPERATOR: 'Operator',
    NUMBER: 'number',
    CRON_EXPRESSION: 'Cron expression',
    DURATION: 'Duration',
    BEARER_TOKEN: 'Bearer token',
    USERNAME: 'Username',
    PASSWORD: 'Password',
    STATUSPAGE_TYPE: 'Statuspage type',
    SUBDOMAIN: 'Subdomain',
    GROUP_NAME: 'Group name',
    COMPONENT_NAME: 'Component name',
    RESOURCE: 'Resource',
    IP_ADDRESSES: 'IP addresses',
    PAGE_NAME: 'Page name',
    WEBSITE_URL: 'Website url',
    PRIVACY_URL: 'Privacy url',
    SUPPORT_URL: 'Support url',
    GA_TRACKING: 'GA tracking id',
    DOMAIN: 'Custom domain',
    URGENCY: 'Urgency',
    RULE_TYPE: 'Rule type',
    CONTENT_TYPE: 'Content type',
    LEVEL: 'Level',
    LOGO: 'logo',
    FAVICON: 'favicon',
    TEAM_MEMBER: 'Team member',
    START_TIME: 'start time',
    END_TIME: 'end time',
    WEEKDAYS: 'Weekdays',
    END_DATE: 'end date',
    START_DATE: 'start date',
    CONDITIONS: 'conditions',
    METRIC_TYPE: 'Metric type'
}

const allowedProtocols = [
    'http',
    'https'
]
const DATE_FORMAT = 'DD.MM.YYYY'
const MIN_LENGTH = 2
const MIN_DESC_LENGTH = 3
const PASSWORD_MIN_LENGTH = 8
const CODE_LENGTH = 6
const MIN_FEEDBACK_LENGTH = 20
const STATUS_CODE_MIN_VALUE = 100
const STATUS_CODE_MAX_VALUE = 599
const MIN_PORT_VALUE = 1
const MAX_PORT_VALUE = 65535
const STATUSPAGE_LOGO_MAX_SIZE = {
    value: 1 * 1024 * 1024,
    label: '1MB'
}
const STATUSPAGE_FAVICON_MAX_SIZE = {
    value: 256 * 1024,
    label: '256KB'
}

const PASSWORD_CHARS = '^(?=.*[!@#$%^&*:;=+|~`}{)(></"_\\[\\],\'.?-])'
const URL_CHARS = '^(?=.*[.])'
const PROTOCOL_CHARS = '^http'
const PORT_VALUES = {
    smtp: [25, 465, 587],
    imap: [143, 993],
    pop: [110, 995]
}

const typesArr = [monitoringTypes.port.value, monitoringTypes.email_protocols.value]

Yup.addMethod(Yup.string, 'host', function(message) {
    return this.test({
        name: 'host',
        message,
        test: value => {
            let url
            try {
                url = new URL(value)
            } catch (e) {
                url = new URL(`https://${value}`)
            }
            return !(url.search || url.port || url.pathname !== '/')
        }
    })
})

const addResourceSchema = Yup.object().shape({
    name: Yup.string()
        .required(text.required(fields.RESOURCE_NAME))
        .min(MIN_LENGTH, text.length(fields.RESOURCE_NAME, MIN_LENGTH, 'minimum')),
    conditionOption: Yup.object(),
    keyword: Yup.string().nullable().when(['conditionOption', 'type'], {
        is: (conditionOption, type) => type !== monitoringTypes.icmp.value && [conditionTypes.does_contain.value, conditionTypes.does_contain.value].includes(conditionOption?.value),
        then: Yup.string().required(text.required(fields.KEYWORD)).trim().min(MIN_LENGTH, text.length(fields.KEYWORD, MIN_LENGTH, 'minimum')).test('len', '', (keywords = '') => {
            const keywordArray = [keywords]
            return keywordArray
        })
    }),
    url: Yup.string()
        .when('type', (data) => {
            if (data !== monitoringTypes.http.value) {
                return Yup.string()
                    .transform(value => value.trim())
                    .required(text.required(fields.HOST))
                    .matches(URL_CHARS, text.invalid_field(fields.HOST))
                    .host(text.invalid_field(fields.HOST))
            }
            return Yup.string()
                .transform(value => value.trim())
                .required(text.required(fields.URL))
                .url(text.invalid_field(fields.URL))
                .matches(PROTOCOL_CHARS, text.has_protocol(allowedProtocols))
                .matches(URL_CHARS, text.invalid_field(fields.URL))
        })
    ,
    service_id: Yup.object().nullable()
        .required(text.required(fields.SERVICE_NAME)),
    team_id: Yup.object().nullable()
        .required(text.required(fields.TEAM_NAME)),
    regions: Yup.array().min(1, text.required(fields.REGIONS, true)),
    headers: Yup.array().nullable().when('is_advanced', (data) => {
        if (data) {
            return Yup.array().nullable().of(Yup.object().shape({
                key: Yup.string().when(['value'], {
                    is: value => value,
                    then: Yup.string().required(text.required(fields.KEY))
                }),
                value: Yup.string().when(['key'], {
                    is: key => key,
                    then: Yup.string().required(text.required(fields.VALUE))
                })
            }, [['key', 'value']]))
        }
    }),
    bearer_token: Yup.string().nullable().when('auth_method', data => {
        if (data?.value === authMethods.bearer_token.value) {
            return Yup.string().required(text.required(fields.BEARER_TOKEN))
        }
    }),
    username: Yup.string().nullable().when('auth_method', data => {
        if (data?.value === authMethods.basic_auth.value) {
            return Yup.string().required(text.required(fields.USERNAME))
        }
    }),
    password: Yup.string().nullable().when('auth_method', data => {
        if (data?.value === authMethods.basic_auth.value) {
            return Yup.string().required(text.required(fields.PASSWORD))
        }
    }),
    type: Yup.string(),
    conditions: Yup.object().nullable().when('conditionOption', (conditionOption) => {
        if (!conditionOption) {
            return Yup.object().shape({})
        }

        return Yup.object().shape({
            [conditionOption.type]: Yup.array().test('check-length', function(data) {
                if (data?.every(({key, value}) => !key && !value) && conditionOption.value === conditionTypes.json_key_value_check.value) {
                    const condition = data[0]
                    const errors = []
                    if (!condition.key) {
                        errors.push(this.createError({path: `${this.path}.[0].key`, message:text.required(fields.KEY)}))
                    }
                    if (!condition.value) {
                        errors.push(this.createError({path: `${this.path}.[0].value`, message:text.required(fields.VALUE)}))
                    }
                    if (!condition.operator) {
                        errors.push(this.createError({path: `${this.path}.[0].operator`, message:text.required(fields.OPERATOR)}))
                    }
                    if (errors.length > 0) {
                        throw new Yup.ValidationError(errors)
                    }
                }
                return true
            }).of(
                Yup.object().shape({
                    key: Yup.string().nullable().when(['value', 'operator'], {
                        is: (value, operator) =>
                            conditionOption.value === conditionTypes.json_key_value_check.value && (operator ? operator && value : value),
                        then: Yup.string().required(text.required(fields.KEY)),
                        otherwise: Yup.string().nullable()
                    }),
                    operator: Yup.object().nullable().when(['value', 'key'], {
                        is: (value, key) => conditionOption.value === conditionTypes.json_key_value_check.value ? value || key : Yup.object().nullable().required(text.required(fields.OPERATOR)),
                        then: Yup.object().nullable().required(text.required(fields.OPERATOR)),
                        otherwise: Yup.object().nullable()
                    }),
                    value: Yup.mixed().when(['key', 'operator'], {
                        is: (key) => conditionOption.value === conditionTypes.json_key_value_check.value ? key : Yup.string().required(text.required(fields.VALUE)),
                        then: Yup.mixed().when([], {
                            is: () => conditionOption.value === conditionTypes.status_code_check.value,
                            then: Yup.number()
                                .typeError(text.must_be_type(fields.STATUS_CODE, fields.NUMBER))
                                .required(text.required(fields.STATUS_CODE))
                                .test(
                                    'is-between-100-599',
                                    text.must_be_between(fields.STATUS_CODE, STATUS_CODE_MIN_VALUE, STATUS_CODE_MAX_VALUE),
                                    value => value >= STATUS_CODE_MIN_VALUE && value <= 599
                                ),
                            otherwise: Yup.string().required(text.required(fields.VALUE))
                        }),
                        otherwise: Yup.mixed()
                    })
                },  [
                    ['key', 'value'], ['key', 'operator'],
                    ['operator', 'value']
                ])
            )
        })
    }),
    monitoring_protocol: Yup.object().nullable().when('type', data => {
        if (typesArr.includes(data)) {
            return Yup.object().nullable().required(text.required(fields.PROTOCOL))
        }
    }),
    port: Yup.number().nullable().when(['type', 'monitoring_protocol'], (type, additionalType) => {
        if ([monitoringTypes.port.value, monitoringTypes.udp.value, monitoringTypes.tcp.value].includes(type)) {
            return Yup.number().nullable().required(text.required(fields.PORT))
                .min(MIN_PORT_VALUE, text.must_be_between(fields.PORT, MIN_PORT_VALUE, MAX_PORT_VALUE))
                .max(MAX_PORT_VALUE, text.must_be_between(fields.PORT, MAX_PORT_VALUE, MAX_PORT_VALUE))
        }
        if (additionalType?.value && Object.values(protocols.smtp_imap_pop3.data[additionalType?.value])) {
            return Yup.number().nullable()
                .required(text.required(fields.PORT))
                .oneOf(PORT_VALUES[additionalType.value], text.one_of(fields.PORT, PORT_VALUES[additionalType.value]))
        }
        return Yup.number().nullable()
    }),
    data: Yup.string().nullable().when(['type', 'conditionOption'], (type, conditionOption) => {
        if (type === monitoringTypes.port.value && conditionOption?.isCondition) {
            return Yup.string().nullable().required(text.required(fields.DATA_TO_SEND))
        }
    }),
    maintenance_cron_expression: Yup.string().transform(value => value.trim())
        .matches(/^(@(annually|yearly|monthly|weekly|daily|hourly|reboot))|(@every (\d+(ns|us|µs|ms|s|m|h))+)|((((\d+,)+\d+|(\d+(\/|-)\d+)|\d+|\*) ?){5,7})$/, text.invalid_field(fields.CRON_EXPRESSION)),
    maintenance_duration: Yup.string()
})

const addDynamicRulesetSchema = (values) => {
    return Yup.object().shape(
        Object.fromEntries(
            Object.entries(values).map(([key, value]) => {
                if (value.pairs) {
                    return [
                        key,
                        Yup.object().shape({
                            pairs: Yup.array().of(
                                Yup.object().shape({
                                    key: Yup.object().nullable().when(['values', 'operator'], {
                                        is: (values, operator) => {
                                            const hasOperator = operator?.value?.value
                                            const hasValues = values?.value || (values?.value?.value && values?.value?.value !== '')
                                            return hasOperator && hasValues
                                        },
                                        then: Yup.object()
                                            .shape({
                                                value: Yup.mixed().test(
                                                    'validate-metric-based-on-type',
                                                    text.required(fields.KEY),
                                                    function(val) {
                                                        const formType = this.parent?.form_type
                                                        if (formType === 'input') {
                                                            return typeof val === 'string' && val.trim() !== ''
                                                        }
                                                        if (formType === 'select') {
                                                            return val !== null && typeof val === 'object' && 'value' in val
                                                        }
                                                        return !!val
                                                    }
                                                )
                                            })
                                            .required(text.required(fields.KEY)),
                                        otherwise: Yup.object().nullable()
                                    }),
                                    operator: Yup.object().nullable().when(['values', 'key'], {
                                        is: (values, key) => {
                                            const hasKey = key?.value || (key?.value?.value && key?.value?.value !== '')
                                            const hasValues = values?.value || (values?.value?.value && values?.value?.value !== '')
                                            return hasKey && hasValues
                                        },
                                        then: (schema) => Yup.object().shape({value: schema.required(text.required(fields.OPERATOR))}),
                                        otherwise: (schema) => schema
                                    }),
                                    values: Yup.object()
                                        .nullable()
                                        .when(['key', 'operator'], {
                                            is: (key, operator) => {
                                                const hasKey = key?.value || (key?.value?.value && key?.value?.value !== '')
                                                const hasOperator = operator?.value?.value
                                                return hasKey && hasOperator
                                            },
                                            then: () => {
                                                let schema = Yup.object().shape({
                                                    value: Yup.mixed().test(
                                                        'validate-condition-value-based-on-type',
                                                        text.required(fields.VALUE),
                                                        function(val) {
                                                            const formType = this.parent?.form_type
                                                            if (this.parent?.type === 'number') {
                                                                return typeof Number(val) === 'number' && !isNaN(val)
                                                            }

                                                            if (formType === 'input') {
                                                                return typeof val === 'string' && val.trim() !== ''
                                                            }
                                                            if (formType === 'select') {
                                                                return val !== null && typeof val === 'object' && 'value' in val
                                                            }
                                                            return !!val
                                                        }
                                                    )
                                                })
                                                schema = schema.when('key', {
                                                    is: (key) => key?.value?.unit === 'percentage',
                                                    then: (s) =>
                                                        s.shape({
                                                            value: Yup.number().required(text.required(fields.VALUE))
                                                                .min(0, text.must_be_between(fields.VALUE, 0, 100))
                                                                .max(100, text.must_be_between(fields.VALUE, 0, 100))
                                                        }),
                                                    otherwise: (s) =>  s
                                                })
                                                return schema.required(text.required(fields.VALUE))
                                            },
                                            otherwise: Yup.object().nullable()
                                        })

                                }, [
                                    ['key', 'values'],
                                    ['key', 'operator'],
                                    ['operator', 'values']
                                ])
                            )
                        })
                    ]
                }

                if (typeof value.value === 'string' && key !== 'message') {
                    return [key,  Yup.object().shape({value: Yup.string().required(text.required(fields[key.toUpperCase()]))})]
                }

                if (typeof value.value === 'object') {
                    return [key, Yup.object().shape({value: Yup.object().nullable().required(text.required(fields[key.toUpperCase()]))})]
                }

                return [key, Yup.object().shape({value: Yup.mixed().nullable()})]
            })
        )
    )
}

const customerSchema = Yup.object().shape({
    name: Yup.string()
        .required(text.required(fields.NAME))
        .min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum')),
    surname: Yup.string()
        .required(text.required(fields.SURNAME))
        .min(MIN_LENGTH, text.length(fields.SURNAME, MIN_LENGTH, 'minimum'))
})

const scheduleSchema = Yup.object().shape({
    team_member: Yup.object().nullable().required(text.required(fields.TEAM_MEMBER)),
    start_time: Yup.string().required(text.choose_field(fields.START_TIME)),
    end_time: Yup.string().required(text.choose_field(fields.START_TIME)).test(
        'is-later-than-start',
        text.end_time_validation('End time', fields.START_TIME),
        function(value) {
            return compareTimes(this.parent.start_time, value) < 0
        }
    ),
    end_date: Yup.string().test(
        'is-later-than-start',
        text.end_time_validation('End date', fields.START_DATE),
        function(value) {
            return DateHelper.convertToTimeZone(this.parent.start_date, DATE_FORMAT) <= DateHelper.convertToTimeZone(value, DATE_FORMAT)
        }
    ),
    weekdays: Yup.array().min(1, text.choose_field(fields.WEEKDAYS))
})

const usernameSchema = Yup.object().shape({
    email: Yup.string()
        .required(text.required(fields.EMAIL))
        .email(text.like_email(fields.EMAIL))
})

const codeSchema = Yup.object().shape({
    code: Yup.string()
        .required(text.required(fields.CODE))
        .matches(`^[0-9]{${CODE_LENGTH}}$`, text.digits(fields.CODE, CODE_LENGTH))
})

const teamSchema = Yup.object().shape({
    name: Yup.string()
        .required(text.required(fields.NAME))
        .min(MIN_LENGTH, text.length(fields.TEAM_NAME, MIN_LENGTH, 'minimum'))
})

const feedbackSchema = Yup.object().shape({
    feedback: Yup.string()
        .required(text.required(fields.FEEDBACK))
        .min(MIN_FEEDBACK_LENGTH, text.length(fields.FEEDBACK, MIN_FEEDBACK_LENGTH, 'minimum'))
})

const notificationEmailSchema = Yup.object().shape({
    value: Yup.string()
        .required(text.required(fields.CHANNEL_EMAIL))
        .email(text.like_email(fields.CHANNEL_EMAIL))
})

const voucherSchema = Yup.object().shape({
    code: Yup.string().nullable()
        .required(text.required(fields.VOUCHER))
})

const couponSchema = Yup.object().shape({
    code: Yup.string().nullable()
        .required(text.required(fields.COUPON))
})

const channelTypeSchema = Yup.object().shape({
    type: Yup.object().nullable()
        .required(text.required(fields.CHANNEL_TYPE))
})

const webhookSchema = Yup.object().shape({
    type: Yup.object().nullable()
        .required(text.required(fields.CHANNEL_TYPE)),
    value: Yup.string()
        .transform(value => value.trim())
        .required(text.required(fields.URL))
        .url(text.invalid_field(fields.URL))
        .matches(URL_CHARS, text.invalid_field(fields.URL))
        .matches(PROTOCOL_CHARS, text.has_protocol(allowedProtocols))
})

const webhookURLSchema = Yup.object().shape({
    value: Yup.string()
        .transform(value => value.trim())
        .required(text.required(fields.WEBHOOK))
        .url(text.invalid_field(fields.WEBHOOK))
        .matches(URL_CHARS, text.invalid_field(fields.WEBHOOK))
        .matches(PROTOCOL_CHARS, text.has_protocol(allowedProtocols))
})

const automationSchema = Yup.object().shape({
    name: Yup.string()
        .required(text.required(fields.NAME))
        .min(MIN_DESC_LENGTH, text.length(fields.NAME, MIN_DESC_LENGTH, 'minimum')),
    description: Yup.string()
        .required(text.required(fields.DESCRIPTION))
        .min(MIN_DESC_LENGTH, text.length(fields.DESCRIPTION, MIN_DESC_LENGTH, 'minimum')),
    events: Yup.array().min(1, text.should_select(fields.EVENT))
})

const reminderSchema = Yup.object().shape({
    days_before: Yup.array().min(1, text.should_select(fields.REMINDER))
})

const deletionReasonSchema = Yup.object().shape({
    reason: Yup.object().nullable()
        .required(text.required(fields.REASON))
})

const validationElement = (message, validator) => {
    return {message, validator: (value, oldValue) => validator(value, oldValue)}
}

const passwordErrors = {
    previous_password: [
        validationElement(realTimeValidationText.length(PASSWORD_MIN_LENGTH, 'minimum'),
            value => validator.isLength(value, PASSWORD_MIN_LENGTH, undefined)),
        validationElement(realTimeValidationText.has_case('lowercase'), value => validator.matches(value, '[a-z]')),
        validationElement(realTimeValidationText.has_case('uppercase'), value => validator.matches(value, '[A-Z]')),
        validationElement(realTimeValidationText.has_number(), value => validator.matches(value, '[0-9]')),
        validationElement(realTimeValidationText.has_char(), value => validator.matches(value, PASSWORD_CHARS))
    ],
    proposed_password: [
        validationElement(realTimeValidationText.length(PASSWORD_MIN_LENGTH, 'minimum'),
            value => validator.isLength(value, PASSWORD_MIN_LENGTH, undefined)),
        validationElement(realTimeValidationText.has_case('lowercase'), value => validator.matches(value, '[a-z]')),
        validationElement(realTimeValidationText.has_case('uppercase'), value => validator.matches(value, '[A-Z]')),
        validationElement(realTimeValidationText.has_number(), value => validator.matches(value, '[0-9]')),
        validationElement(realTimeValidationText.has_char(), value => validator.matches(value, PASSWORD_CHARS))
    ],
    confirm_new_password: [
        validationElement(realTimeValidationText.length(PASSWORD_MIN_LENGTH, 'minimum'),
            value => validator.isLength(value, PASSWORD_MIN_LENGTH, undefined)),
        validationElement(realTimeValidationText.has_case('lowercase'), value => validator.matches(value, '[a-z]')),
        validationElement(realTimeValidationText.has_case('uppercase'), value => validator.matches(value, '[A-Z]')),
        validationElement(realTimeValidationText.has_number(), value => validator.matches(value, '[0-9]')),
        validationElement(realTimeValidationText.has_char(), value => validator.matches(value, PASSWORD_CHARS)),
        validationElement(realTimeValidationText.is_same(fields.PROPOSED_PASSWORD), (value, oldValue) => value === oldValue)
    ]
}

const addMemberSchema = Yup.object().shape({
    channel_type: Yup.object().nullable()
        .required(text.required(fields.CHANNEL_TYPE)),
    channel_value: Yup.string()
        .required(text.required(fields.MEMBER_EMAIL))
        .email(text.like_email(fields.MEMBER_EMAIL))
})

const addTeamSchema = Yup.object().shape({
    name: Yup.string()
        .required(text.required(fields.NAME))
        .min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum'))
})

const addJobSchema = Yup.object().shape({
    name: Yup.string()
        .required(text.required(fields.NAME))
        .min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum')),
    service_id: Yup.object().nullable()
        .required(text.required(fields.SERVICE_NAME)),
    methods: Yup.array().min(1, text.required(fields.METHODS, true))
})

const statuspageIoSchema = Yup.object().shape({
    name: Yup.string().required(text.required(fields.NAME)).min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum')),
    api_key: Yup.string().required(text.required(fields.API_KEY)).min(MIN_LENGTH, text.length(fields.API_KEY, MIN_LENGTH, 'minimum')),
    type: Yup.object().nullable().required(text.required(fields.INTEGRATION_TYPE)),
    page_id: Yup.string().required(text.required(fields.INTEGRATION_PAGE_ID)).min(MIN_LENGTH, text.length(fields.INTEGRATION_PAGE_ID, MIN_LENGTH, 'minimum'))
})

const statusIoSchema = Yup.object().shape({
    name: Yup.string().required(text.required(fields.NAME)).min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum')),
    api_key: Yup.string().required(text.required(fields.API_KEY)).min(MIN_LENGTH, text.length(fields.API_KEY, MIN_LENGTH, 'minimum')),
    api_id: Yup.string().required(text.required(fields.API_ID)).min(MIN_LENGTH, text.length(fields.API_ID, MIN_LENGTH, 'minimum')),
    type: Yup.object().nullable().required(text.required(fields.INTEGRATION_TYPE)),
    page_id: Yup.string().required(text.required(fields.INTEGRATION_PAGE_ID)).min(MIN_LENGTH, text.length(fields.INTEGRATION_PAGE_ID, MIN_LENGTH, 'minimum'))
})

const resourceIntegrationSchema = Yup.object().shape({
    type: Yup.object().nullable().required(text.required(fields.INTEGRATION_TYPE)),
    integration: Yup.object().nullable().required(text.required(fields.INTEGRATION)),
    components: Yup.array().min(1, text.required(fields.COMPONENTS, true)),
    name: Yup.string().required(text.required(fields.NAME)).min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum')),
    status: Yup.object().nullable().required(text.required(fields.STATUS))
})

const statuspageAdvancedSchema = Yup.object().shape({
    description: Yup.string().nullable().min(MIN_LENGTH, text.length(fields.DESCRIPTION, MIN_LENGTH, 'minimum')),
    page_name: Yup.string().nullable().min(MIN_LENGTH, text.length(fields.PAGE_NAME, MIN_LENGTH, 'minimum')),
    website_url: Yup.string().transform(value => value.trim()).nullable().url(text.invalid_field(fields.WEBSITE_URL)),
    privacy_url: Yup.string().transform(value => value.trim()).nullable().url(text.invalid_field(fields.PRIVACY_URL)),
    support_url: Yup.string().transform(value => value.trim()).nullable().url(text.invalid_field(fields.SUPPORT_URL)),
    ga_tracking: Yup.string().nullable().min(MIN_LENGTH, text.length(fields.GA_TRACKING, MIN_LENGTH, 'minimum')),
    logo: Yup.mixed().nullable()
        .test('fileType', text.invalid_file_type(fields.LOGO), value => {
            if (!value) {
                return true
            }
            return value.type && value.type.includes('image/')
        })
        .test('fileSize', text.invalid_file_size(fields.LOGO, STATUSPAGE_LOGO_MAX_SIZE.label), value => {
            if (!value) {
                return true
            }
            const maxSizeInBytes = STATUSPAGE_LOGO_MAX_SIZE.value
            return value.size <= maxSizeInBytes
        }),
    favicon: Yup.mixed().nullable()
        .test('fileType', text.invalid_file_type(fields.FAVICON), value => {
            if (!value) {
                return true
            }
            return value.type && (value.type.includes('image/png') || value.type.includes('image/x-icon'))
        })
        .test('fileSize', text.invalid_file_size(fields.FAVICON, STATUSPAGE_FAVICON_MAX_SIZE.label), value => {
            if (!value) {
                return true
            }
            const maxSizeInBytes = STATUSPAGE_FAVICON_MAX_SIZE.value
            return value.size <= maxSizeInBytes
        })
        .test('dimension', text.invalid_dimension(fields.FAVICON), async value => {
            if (!value) {
                return true
            }

            try {
                const img = new Image()
                img.src = URL.createObjectURL(value)

                const loadImage = () => {
                    return new Promise((resolve, reject) => {
                        img.onload = () => resolve(img)
                        img.onerror = () => reject(new Error(text.failed_load_file(fields.FAVICON)))
                    })
                }

                await loadImage()

                return img.width === img.height
            } catch (error) {
                return false
            }
        })
})

const statuspageDomainSchema = Yup.object().shape({
    domain: Yup.string().nullable().min(MIN_LENGTH, text.length(fields.DOMAIN, MIN_LENGTH, 'minimum'))
})

const statuspageSettingsSchema = Yup.object().shape({
    name: Yup.string().required(text.required(fields.NAME)).min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum')),
    type: Yup.object().nullable().required(text.required(fields.STATUSPAGE_TYPE)),
    ip_addresses: Yup.string().nullable().when(['type', 'ip_addresses', 'password'], (type, ipAddresses, password) => {
        if (type?.value === 'private' && ipAddresses) {
            return Yup.string().test('ip_addresses', text.invalid_field(fields.IP_ADDRESSES, true), (value) => {
                const lines = value?.split('\n')

                return lines?.every(line => regexPatterns.ipv4WithCIDRPattern.test(line?.trim()))
            })
        }
        if (type?.value === 'private' && !password && !ipAddresses) {
            return Yup.string().required(text.required(fields.IP_ADDRESSES, true))
        }
    }),
    password: Yup.string().nullable().when(['type', 'ip_addresses', 'password'], (type, ipAddresses, password) => {
        if (type?.value === 'private' && password) {
            return Yup.string().min(PASSWORD_MIN_LENGTH, text.length(fields.PASSWORD, PASSWORD_MIN_LENGTH, 'minimum'))
        }
        if (type?.value === 'private' && !ipAddresses && !password) {
            return Yup.string().required(text.required(fields.PASSWORD))
        }
    }),
    subdomain: Yup.string().nullable().required(text.required(fields.SUBDOMAIN))
}, [
    ['ip_addresses', 'password', 'ip_addresses'],
    ['password', 'ip_addresses', 'password'],
    ['ip_addresses', 'ip_addresses', 'password'],
    ['password', 'password', 'ip_addresses']
])

const statuspageStructureSchema = Yup.object().shape({
    structures: Yup.array().of(
        Yup.object().shape(
            {
                is_group: Yup.object({
                    value: Yup.boolean(),
                    label: Yup.string()
                }),
                group_name: Yup.string().nullable().when('is_group', data => {
                    if (data?.value) {
                        return Yup.string().required(text.required(fields.GROUP_NAME)).min(MIN_LENGTH, text.length(fields.GROUP_NAME, MIN_LENGTH, 'minimum'))
                    }
                }),
                type: Yup.object({
                    value: Yup.string(),
                    label: Yup.string()
                }),
                components: Yup.array().nullable().when('type', data => {
                    if (data?.value === 'resource') {
                        return Yup.array().min(1, text.required(fields.RESOURCE))
                    }
                }),
                custom_components: Yup.array().when('type', data => {
                    if (data?.value === 'custom') {
                        return Yup.array().of(
                            Yup.object().shape({
                                name: Yup.string().required(text.required(fields.COMPONENT_NAME)).min(MIN_LENGTH, text.length(fields.COMPONENT_NAME, MIN_LENGTH, 'minimum')),
                                description: Yup.string().required(text.required(fields.DESCRIPTION)).min(MIN_LENGTH, text.length(fields.DESCRIPTION, MIN_LENGTH, 'minimum'))
                            })
                        )
                    }
                })
            }
        )
    )
})

const incidentSchema = Yup.object().shape({
    name: Yup.string().required(text.required(fields.NAME)).min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum')),
    description: Yup.string().required(text.required(fields.DESCRIPTION)).min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum')),
    urgency: Yup.object().nullable().required(text.required(fields.URGENCY)),
    components: Yup.array().nullable().when('actions', data => {
        if (data.includes('trigger-statuspage-update')) {
            return Yup.array().min(1, text.required(fields.COMPONENTS, true))
        }
    })
})

const serverMonitoringSchema = (formValues) => {
    let schema = Yup.object()
    Object.keys(formValues).forEach(field => {
        switch (field) {
        case 'server_name':
            schema = schema.shape({
                [field]: Yup.string().required(text.required(fields.NAME)).min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum'))
            })
            break
        case 'description':
            schema = schema.shape({
                [field]: Yup.string().required(text.required(fields.DESCRIPTION)).min(MIN_LENGTH, text.length(fields.DESCRIPTION, MIN_LENGTH, 'minimum'))
            })
            break
        case 'service_id':
            schema = schema.shape({
                [field]: Yup.object().test('is-not-empty', text.required(fields.SERVICE_NAME), ({value}) => {
                    return value
                })
            })
            break
        case 'team_id':
            schema = schema.shape({
                [field]: Yup.object().nullable().required(text.required(fields.TEAM_NAME), ({value}) => {
                    return value
                })
            })
            break
        default:
            break
        }
    })

    return schema
}

const rulesetSchema = Yup.object().shape({
    name: Yup.string().required(text.required(fields.NAME)).min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum')),
    description: Yup.string().required(text.required(fields.DESCRIPTION)).min(MIN_LENGTH, text.length(fields.NAME, MIN_LENGTH, 'minimum')),
    rule_type: Yup.object().nullable().required(text.required(fields.RULE_TYPE)),
    content_type: Yup.object().nullable().required(text.required(fields.CONTENT_TYPE)),
    urgency: Yup.object().nullable().required(text.required(fields.URGENCY)),
    level: Yup.object().nullable().required(text.required(fields.LEVEL)),
    components: Yup.array().nullable().when('actions', data => {
        if (data.includes('trigger-statuspage-update')) {
            return Yup.array().min(1, text.required(fields.COMPONENTS, true))
        }
    }),
    webhook_conditions: Yup.array().when(['rule_type', 'content_type'], (ruleType, contentType) => {
        if (ruleType.value === 'webhook') {
            return Yup.array().of(
                Yup.object().shape({
                    key: Yup.string().when(['value', 'operator'], {
                        is: (operator, value) => contentType.value === 'json-check' && (operator || value),
                        then: Yup.string().required(text.required(fields.KEY))
                    }),
                    operator: Yup.object().nullable().when(['key', 'value'], {
                        is: (key, value) => (contentType.value === 'json-check' && key) || value,
                        then: Yup.object().nullable().required(text.required(fields.OPERATOR))
                    }),
                    value: Yup.string().when(['key', 'operator'], {
                        is: (key, operator) => (contentType.value === 'json-check' && key) || operator,
                        then: Yup.string().required(text.required(fields.VALUE))
                    })
                }, [
                    ['key', 'value'], ['key', 'operator'],
                    ['operator', 'value']
                ])
            ).test('at-least-one', 'At least one condition is required', data => {
                const filteredData = data?.filter(condition => Object.values(condition).some(value => value !== undefined && value !== null))
                return !!filteredData?.length
            }).test('max-size', 'Conditions limit exceeded. Max size is 5', data => {
                const filteredData = data?.filter(condition => Object.values(condition).some(value => value !== undefined && value !== null))
                return filteredData?.length <= 5
            })
        }
    })
})

export {
    addResourceSchema,
    customerSchema,
    usernameSchema,
    codeSchema,
    teamSchema,
    feedbackSchema,
    notificationEmailSchema,
    voucherSchema,
    channelTypeSchema,
    webhookSchema,
    webhookURLSchema,
    automationSchema,
    reminderSchema,
    deletionReasonSchema,
    passwordErrors,
    addMemberSchema,
    addTeamSchema,
    addJobSchema,
    statuspageIoSchema,
    statusIoSchema,
    resourceIntegrationSchema,
    couponSchema,
    statuspageSettingsSchema,
    statuspageStructureSchema,
    statuspageAdvancedSchema,
    statuspageDomainSchema,
    incidentSchema,
    rulesetSchema,
    addDynamicRulesetSchema,
    scheduleSchema,
    serverMonitoringSchema
}
